import type { GenerationType } from '@/api/types';

export type TemplateCategory = 'trends' | 'classic';
export type TemplateBadge = 'popular' | 'new';

export interface TemplatePreset {
  model_key: string;
  prompt: string;
  duration?: number;
  resolution?: '480p' | '720p' | '1080p';
  aspect_ratio?: string;
  generate_audio?: boolean;
}

export interface TemplateExample {
  url: string;
  poster?: string;
}

export interface Template {
  id: string;
  title: string;
  description: string;
  category: TemplateCategory;
  badge?: TemplateBadge;
  kind: GenerationType;
  preset: TemplatePreset;
  examples: TemplateExample[];
  hint_first_frame?: 'required' | 'optional' | 'none';
}

export const TEMPLATES: readonly Template[] = [
  {
    id: 'korean-dance',
    title: 'КОРЕЙСКИЙ ТАНЕЦ',
    description: 'Энергичный k-pop танец с фирменной хореографией.',
    category: 'trends',
    badge: 'popular',
    kind: 'video',
    preset: {
      model_key: 'seedance_2_fast',
      prompt: 'Energetic k-pop dance choreography, sharp synchronized moves, vibrant studio lighting, dynamic camera. Keep face and outfit consistent.',
      duration: 10,
      resolution: '720p',
      aspect_ratio: '9:16',
    },
    examples: [{ url: '/templates/korean-dance/1.mp4', poster: '/templates/korean-dance/poster.jpg' }],
    hint_first_frame: 'required',
  },
  {
    id: 'lush-life',
    title: 'ХОЧУ СКАЧАТЬ',
    description: 'Игривый ритмичный танец под Lush Life — для фана и сторис.',
    category: 'trends',
    badge: 'new',
    kind: 'video',
    preset: {
      model_key: 'seedance_2_fast',
      prompt: 'Playful upbeat dance, bouncy hips and shoulder moves, soft natural lighting, social-media vibe. Keep face and clothing identity intact.',
      duration: 10,
      resolution: '720p',
      aspect_ratio: '9:16',
    },
    examples: [{ url: '/templates/lush-life/1.mp4', poster: '/templates/lush-life/poster.jpg' }],
    hint_first_frame: 'required',
  },
  {
    id: 'take-phone',
    title: 'ВОЗЬМИ ТЕЛЕФОН ДЕТКА',
    description: 'Трендовый липсинк с твоего фото — берёт телефон и подпевает в кадр.',
    category: 'trends',
    badge: 'popular',
    kind: 'video',
    preset: {
      model_key: 'seedance_2_fast',
      prompt: 'The person picks up a phone from the table, brings it close to the face and lip-syncs to a catchy upbeat track. Confident playful expression, eye contact with camera, casual indoor lighting. Keep face and outfit identity intact.',
      duration: 10,
      resolution: '720p',
      aspect_ratio: '9:16',
    },
    examples: [{ url: '/templates/take-phone/1.mp4', poster: '/templates/take-phone/poster.jpg' }],
    hint_first_frame: 'required',
  },
  {
    id: 'want-to-say',
    title: 'Я ХОЧУ СКАЗАТЬ',
    description: 'Танец-липсинк под трендовый трек — для рилсов и сторис.',
    category: 'trends',
    badge: 'new',
    kind: 'video',
    preset: {
      model_key: 'seedance_2_fast',
      prompt: 'Trendy lip-sync dance, expressive facial mime, rhythmic shoulder and hand gestures, soft ambient lighting, social-media energy. Keep face and clothing identity intact.',
      duration: 10,
      resolution: '720p',
      aspect_ratio: '9:16',
    },
    examples: [{ url: '/templates/want-to-say/1.mp4', poster: '/templates/want-to-say/poster.jpg' }],
    hint_first_frame: 'required',
  },
  {
    id: 'valera-dance',
    title: 'ВАЛЕРА',
    description: 'Фирменный задорный танец «Валера» с твоего фото.',
    category: 'trends',
    kind: 'video',
    preset: {
      model_key: 'seedance_2_fast',
      prompt: 'Goofy energetic dance with bouncy steps, swinging arms and exaggerated facial expressions, vibrant lively atmosphere. Keep face and outfit identity intact.',
      duration: 10,
      resolution: '720p',
      aspect_ratio: '9:16',
    },
    examples: [{ url: '/templates/valera-dance/1.mp4', poster: '/templates/valera-dance/poster.jpg' }],
    hint_first_frame: 'required',
  },
  {
    id: 'couple-collage',
    title: 'ТРЕНДОВЫЙ КОЛЛАЖ',
    description: 'Стильный двойной коллаж с двумя людьми в монохромных тонах.',
    category: 'trends',
    badge: 'new',
    kind: 'image',
    preset: {
      model_key: 'nano_banana_2',
      prompt: 'Stylish split-frame editorial collage of two people side by side, soft monochrome studio lighting, gentle grain, magazine-cover composition with a subtle gap between the two halves. Preserve faces.',
    },
    examples: [{ url: '/templates/couple-collage/1.jpg', poster: '/templates/couple-collage/poster.jpg' }],
    hint_first_frame: 'required',
  },
] as const;

export function findTemplate(id: string): Template | null {
  return TEMPLATES.find((t) => t.id === id) ?? null;
}

export function templatesByCategory(category: TemplateCategory): Template[] {
  return TEMPLATES.filter((t) => t.category === category);
}
